import { create } from "zustand";
import { persist } from "zustand/middleware";

export enum LayoutMode {
  PRD = "PRD",
  Design = "Design",
  Code = "Code",
}

export enum LeftTab {
  AIChat = "AIChat",
  DomTree = "DomTree",
  Assets = "Assets",
  FileSelector = "FileSelector",
  Git = "Git",
  EngSpec = "EngSpec",
  History = "History",
  SupabaseConnect = "SupabaseConnect",
  LocalEditing = "LocalEditing",
  Info = "Info",
  FigmaExtension = "FigmaExtension",
  Vercel = "Vercel",
  Convex = "Convex",
  AppStore = "AppStore",
  MCPStore = "MCPStore",
  ProjectSettings = "ProjectSettings",
  ProjectSettingsAdvanced = "ProjectSettingsAdvanced",
  Community = "Community",
}

export enum RightTab {
  Styles = "Styles",
  Props = "Props",
}

export enum ProjectPageNav {
  AIChat = "AIChat",
  Info = "Info",
  Settings = "Settings",
  Views = "Views",
}

interface LayoutState {
  leftExpanded: boolean;
  rightExpanded: boolean;

  layoutMode: LayoutMode;
  leftTab: LeftTab;
  rightTab: RightTab;

  // When changing from away from code or design mode,
  // save the previous mode so the user returns to it
  savedDesignLeftTab: LeftTab | null;
  savedCodeLeftTab: LeftTab | null;

  // For the code + eng spec mode
  showAiCodeChange: boolean;

  // For the PRD mode
  addContextMode: boolean;

  // Track the sizes of the panels
  leftPanelSize: number;
  leftPanelExtensionSize: number;
  canvasSize: number;
  rightPanelSize: number;

  leftPrimarySize: number;
  leftSecondarySize: number;

  expandLeft: () => void;
  collapseLeft: () => void;
  expandRight: () => void;
  collapseRight: () => void;

  setNewLayout: (leftTab?: LeftTab, layoutMode?: LayoutMode) => void;
  setRightTab: (tab: RightTab) => void;

  updateSize: (
    field:
      | "leftPanelSize"
      | "leftPanelExtensionSize"
      | "canvasSize"
      | "rightPanelSize"
      | "leftPrimarySize"
      | "leftSecondarySize",
    size: number,
  ) => void;

  setShowAiCodeChange: (showAiCodeChange: boolean) => void;
  setAddContextMode: (addContextMode: boolean) => void;

  resetLayoutStore: () => void;

  projectPageNav: ProjectPageNav | null;
  setProjectPageNav: (nav: ProjectPageNav | null) => void;

  scrollToStatus: string | null;
  setScrollToStatus: (status: string | null) => void;
}

const useLayoutStore = create<LayoutState>()(
  persist(
    (set, get) => {
      return {
        leftExpanded: true,
        rightExpanded: true,

        layoutMode: LayoutMode.PRD,
        leftTab: LeftTab.AIChat,
        rightTab: RightTab.Styles,

        savedDesignLeftTab: null,
        savedCodeLeftTab: null,

        // Initialize panel sizes to 0 - they are reset whenever the layout changes
        leftPanelSize: 0,
        leftPanelExtensionSize: 0,
        canvasSize: 0,
        rightPanelSize: 0,
        leftPrimarySize: 0,
        leftSecondarySize: 0,

        // For the eng spec view, show or hide the actual diff
        showAiCodeChange: false,

        // In PRD mode only
        addContextMode: false,

        expandLeft: () => set({ leftExpanded: true }),

        collapseLeft: () => set({ leftExpanded: false }),

        expandRight: () => set({ rightExpanded: true }),

        collapseRight: () => set({ rightExpanded: false }),

        setNewLayout: (leftTab?: LeftTab, layoutMode?: LayoutMode) => {
          const currentLayoutMode = get().layoutMode;
          const currentLeftTab = get().leftTab;

          /**
           * Save the previous left tab state when returning to the current layout mode
           */
          const saveLeftTabState = () => {
            if (
              currentLayoutMode === LayoutMode.Design &&
              [
                LeftTab.DomTree,
                LeftTab.AIChat,
                LeftTab.Assets,
                LeftTab.Community,
              ].includes(currentLeftTab)
            ) {
              set({ savedDesignLeftTab: currentLeftTab });
            } else if (
              currentLayoutMode === LayoutMode.Code &&
              [
                LeftTab.DomTree,
                LeftTab.AIChat,
                LeftTab.EngSpec,
                LeftTab.FileSelector,
                LeftTab.Git,
                LeftTab.History,
                LeftTab.SupabaseConnect,
                LeftTab.LocalEditing,
              ].includes(currentLeftTab)
            ) {
              set({ savedCodeLeftTab: currentLeftTab });
            }
          };

          // User clicked the left tab button
          if (leftTab) {
            if (
              leftTab === currentLeftTab &&
              (!layoutMode || layoutMode === currentLayoutMode)
            ) {
              return;
            }

            saveLeftTabState();

            // Set the correct layout mode if it needs to change
            // Note - there might be a provided desired layout mode as well
            let newLayoutMode = layoutMode || currentLayoutMode;
            if (
              [LeftTab.Assets, LeftTab.Community].includes(leftTab) &&
              newLayoutMode !== LayoutMode.Design
            ) {
              newLayoutMode = LayoutMode.Design;
            } else if (
              [
                LeftTab.EngSpec,
                LeftTab.FileSelector,
                LeftTab.Git,
                LeftTab.History,
              ].includes(leftTab) &&
              newLayoutMode !== LayoutMode.Code
            ) {
              newLayoutMode = LayoutMode.Code;
            } else if (
              [LeftTab.DomTree].includes(leftTab) &&
              layoutMode === LayoutMode.PRD
            ) {
              newLayoutMode = LayoutMode.Design;
            }

            set({
              leftTab,
              layoutMode: newLayoutMode,
            });
            return;
          }

          // User clicked the layout mode at the top
          if (layoutMode) {
            if (layoutMode === currentLayoutMode) {
              return;
            }

            saveLeftTabState();

            // Fetch the previously saved layout mode
            const stayInAiChat = currentLeftTab === LeftTab.AIChat;
            if (layoutMode === LayoutMode.Design) {
              set({
                leftTab: stayInAiChat
                  ? currentLeftTab
                  : get().savedDesignLeftTab || LeftTab.DomTree,
                layoutMode,
              });
            } else if (layoutMode === LayoutMode.Code) {
              set({
                leftTab: stayInAiChat
                  ? currentLeftTab
                  : get().savedCodeLeftTab || LeftTab.FileSelector,
                layoutMode,
              });
            } else {
              set({
                leftTab: LeftTab.AIChat,
                layoutMode,
              });
            }
          }
        },

        setRightTab: (tab) => set({ rightTab: tab }),

        setShowAiCodeChange: (showAiCodeChange: boolean) =>
          set({ showAiCodeChange }),

        setAddContextMode: (addContextMode: boolean) => set({ addContextMode }),

        updateSize: (
          field:
            | "leftPanelSize"
            | "leftPanelExtensionSize"
            | "canvasSize"
            | "rightPanelSize"
            | "leftPrimarySize"
            | "leftSecondarySize",
          size: number,
        ) => {
          set({ [field]: size });
        },

        resetLayoutStore: () => {
          set({
            leftExpanded: true,
            rightExpanded: true,
            layoutMode: LayoutMode.PRD,
            leftTab: LeftTab.AIChat,
            rightTab: RightTab.Styles,
            savedDesignLeftTab: null,
            savedCodeLeftTab: null,
            showAiCodeChange: false,
            addContextMode: false,
            projectPageNav: ProjectPageNav.AIChat,
          });
        },

        projectPageNav: ProjectPageNav.AIChat,
        setProjectPageNav: (nav: ProjectPageNav) =>
          set({ projectPageNav: nav }),

        scrollToStatus: null,
        setScrollToStatus: (status: string | null) =>
          set({ scrollToStatus: status }),
      };
    },
    {
      name: "layout-storage",
      partialize: (state) => ({
        leftPanelSize: state.leftPanelSize,
        leftPanelExtensionSize: state.leftPanelExtensionSize,
        rightPanelSize: state.rightPanelSize,
        // canvas size not needed because it fills the remaining space
      }),
    },
  ),
);

export const useNavigateToAIChat = () => {
  const setNewLayout = useLayoutStore((state) => state.setNewLayout);
  const currentLeftTab = useLayoutStore((state) => state.leftTab);

  const navigateToAIChat = (layoutMode?: LayoutMode) => {
    if (currentLeftTab === LeftTab.AIChat) {
      // Force focus on the chat input if already in chat tab
      const chatInput = document.querySelector(".mentions-input__input");
      if (chatInput && "focus" in chatInput) {
        (chatInput as HTMLTextAreaElement).focus();
      }
    }

    setNewLayout(LeftTab.AIChat, layoutMode);
  };

  return navigateToAIChat;
};

export default useLayoutStore;
